import { inject, injectable, injectFromBase } from 'inversify'
import BaseFormService from '@/services/forms/baseFormService'
import { SERVICE_TYPES } from '@/core/container/types'
import type FormBuilderService from '@/services/forms/baseForm/formBuilderService'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ValidationRulesBuilderService from '@/services/validation/validationRulesBuilderService'
import type LoggerService from '@/services/loggerService'
import type { SelectOption } from '@/services/forms/types'
import type MarketingDataService from '@/services/domain/marketingData/marketingDataService'
import {
  MARKETING_DATA_DESCRIPTION_MAX_LENGTH,
  MARKETING_DATA_INDUSTRY_OPTIONS,
  MARKETING_DATA_OTHER_SOURCE,
  MARKETING_DATA_SOURCE_OPTIONS,
  MARKETING_DATA_USE_CASE_OPTIONS,
  type MarketingDataFormScheme,
} from '@/services/domain/marketingData/types'

@injectable()
@injectFromBase()
export default class MarketingDataFormService extends BaseFormService<
  FormBuilderService,
  never,
  MarketingDataFormScheme
> {
  constructor(
    @inject(SERVICE_TYPES.FormBuilderFactoryService) formBuilderFactoryService: () => FormBuilderService,
    @inject(SERVICE_TYPES.EntityManager) protected readonly em: EntityManagerService,
    @inject(SERVICE_TYPES.ValidationRulesBuilderService)
    protected readonly validationRulesBuilderService: ValidationRulesBuilderService,
    @inject(SERVICE_TYPES.LoggerService) protected readonly logger: LoggerService,
    @inject(SERVICE_TYPES.MarketingDataService) protected readonly marketingDataService: MarketingDataService,
  ) {
    super(formBuilderFactoryService, em, validationRulesBuilderService, logger)
  }

  public async buildForm() {
    const builder = this.getBuilder()
    const initial = this.marketingDataService.getMarketingData()

    builder.control({
      fieldType: 'select',
      name: 'industry',
      value: initial?.industry ?? '',
      options: () => this.toSelectOptions(MARKETING_DATA_INDUSTRY_OPTIONS),
      validators: this.validationRulesBuilderService
        .createRules()
        .required({ messagePath: 'marketingDataModal.errors.industryRequired' }),
      // Fix empty options issue when prefilled value from tail of options.
      componentProps: {
        menuFit: () => false,
      },
    })

    builder.control({
      fieldType: 'select',
      name: 'useCase',
      value: initial?.useCase ?? '',
      options: () => this.toSelectOptions(MARKETING_DATA_USE_CASE_OPTIONS),
      validators: this.validationRulesBuilderService
        .createRules()
        .required({ messagePath: 'marketingDataModal.errors.useCaseRequired' }),
      // Fix empty options issue when prefilled value from tail of options.
      componentProps: {
        menuFit: () => false,
      },
    })

    builder.control({
      fieldType: 'select',
      name: 'source',
      value: initial?.source ?? '',
      options: () =>
        MARKETING_DATA_SOURCE_OPTIONS.map((option) => ({ value: option.value, text: option.label ?? option.value })),
      validators: this.validationRulesBuilderService
        .createRules()
        .required({ messagePath: 'marketingDataModal.errors.sourceRequired' }),
      // Fix empty options issue when prefilled value from tail of options.
      componentProps: {
        menuFit: () => false,
      },
    })

    builder.control({
      fieldType: 'textarea',
      name: 'description',
      value: initial?.description ?? '',
      validators: this.validationRulesBuilderService
        .createRules()
        .maxLength(MARKETING_DATA_DESCRIPTION_MAX_LENGTH, {
          messagePath: 'marketingDataModal.errors.descriptionMaxLength',
        })
        .custom(
          'requiredWhenOther',
          (value, formValues) => {
            const source = (formValues as Partial<MarketingDataFormScheme>)?.source
            if (source !== MARKETING_DATA_OTHER_SOURCE) {
              return true
            }
            return typeof value === 'string' && value.trim().length > 0
          },
          { messagePath: 'marketingDataModal.errors.descriptionRequired' },
        ),
    })
  }

  public async submit(formData: MarketingDataFormScheme): Promise<void> {
    const isOther = formData.source === MARKETING_DATA_OTHER_SOURCE
    await this.marketingDataService.submit({
      industry: formData.industry,
      useCase: formData.useCase,
      source: formData.source,
      description: isOther ? formData.description : null,
    })
  }

  private toSelectOptions(values: string[]): SelectOption[] {
    return values.map((value) => ({ value, text: value }))
  }
}
